import { useMemo } from 'react';

import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@mui/material';

import DeviceIcon from 'src/components/fragments/DeviceIcon';
import { DeviceBatteryStatusType, DeviceBatteryStatuses } from 'src/constants/ButtonQa';
import { getDeviceIconType } from 'src/hooks/useDeviceAllocationProperties';
import type { DeviceAllocation } from 'src/store/sageAdminApi';
import { DeviceQaSession } from 'src/store/sageDeviceAuditApi';

interface ButtonQaIconProps {
    deviceQaSession: DeviceQaSession;
    deviceAllocation?: DeviceAllocation;
    latestActivityTimestamp?: number;
    batteryStatus?: DeviceBatteryStatusType;
}

export default function ButtonQaIcon(props: ButtonQaIconProps) {
    const {
        deviceQaSession,
        deviceAllocation,
        latestActivityTimestamp = 0,
        batteryStatus = DeviceBatteryStatuses.Unknown,
    } = props;
    const { status, sessionStartTimeInEpochMs = 0 } = deviceQaSession;

    const hasRecentDeviceEvents = useMemo(
        () => (latestActivityTimestamp ?? 0) > sessionStartTimeInEpochMs,
        [latestActivityTimestamp, sessionStartTimeInEpochMs]
    );

    const isSuccessful =
        status === 'SUCCESS' ||
        (status === 'IN_PROGRESS' &&
            hasRecentDeviceEvents &&
            batteryStatus === DeviceBatteryStatuses.Normal);

    const isFailed =
        status === 'FAILED' ||
        (hasRecentDeviceEvents &&
            (batteryStatus === DeviceBatteryStatuses.LowBattery ||
                batteryStatus === DeviceBatteryStatuses.Unknown));

    const showStatusIcon = isSuccessful || isFailed;

    return (
        <Badge
            badgeContent={
                <FontAwesomeIcon icon={isSuccessful ? faCheck : faXmark} color="white" size="xs" />
            }
            color={isSuccessful ? 'success' : 'error'}
            invisible={!showStatusIcon}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            overlap="circular"
        >
            <DeviceIcon iconType={getDeviceIconType(deviceAllocation)} />
        </Badge>
    );
}
